import React from 'react';
import Footer from './Footer';

const sections = [
    {
        title: 'Prices',
        content: [
            'Published prices are based on costs at the time of publication. We reserve the right to change published prices at any time before your booking is made. We reserve the right to increase the cost of your holiday if the Government imposes new taxes or increases existing taxes, the currency fluctuates, or fuel charges increase. We will inform you of any increases.',
            'Discounts, including Return Customer Discount, cannot be combined with other discounts and/or promotional prices. The greater discount will always apply.',
        ],
    },
    {
        title: 'Payments',
        content: [
            'A non-refundable deposit of 10%, must be made and a confirmation issued by the Company before any binding contract exists. Once we receive your deposit we will invoice you for the remainder of the tour cost. This must be paid not less than 30 days before the start of the tour (or by an alternative date agreed between you and the Company). If you book less than 30 days before departure, you must pay for the tour in full as soon as you book, unless you make alternative arrangements with the Company. If you do not pay your balance by the agreed time, the Company reserves the right to cancel your booking or pursue any unpaid monies.',
        ],
    },
    {
        title: 'Cancellation Policy',
        content: [
            'Payment for the tour is a fundamental part of the tour\'s income and contributes to the overall cost of equipment, running costs, staffing, administration, etc. As the Company will have incurred the majority of its costs before the tour starts, we need to impose cancellation charges to cover these costs.',
            'Deposits are non-refundable. If you cancel your booking, we will impose cancellation charges as follows:',
        ],
        list: [
            'More than 30 days before departure: loss of deposit',
            'Between 30 and 22 days before departure: 35% of the tour price*',
            'Between 21 and 8 days before departure: 50% of the tour price*',
            'Between 7 and 4 days before departure: 75% of the tour price*',
            'Within 3 days before departure or no show: 100% of the tour price*',
        ],
        note: '* or deposit if greater.',
        contentAfter: [
            'Tailor-made tours may be subject to alternative cancellation terms, which will be communicated to applicable Clients at the time of booking.',
            'All cancellations must be in writing and be made by the person who submitted the booking form. Please send an email to your contact at the Company, with a copy to info@trvlland.com.',
            'Once the tour has started, no refund for any unused portion or part of the tour or services to be provided will be given. If you want to make any changes to the tour, or depart the tour early, such alteration or departure will be entirely at your own expense and liability. You will also need to communicate in writing to the tour operator your reason for leaving the tour.',
        ],
    },
    {
        title: 'Cancellation by the Company',
        content: [
            'We reserve the right to cancel the contract between us for any reason before full payment for the trip has been received. In the event of such cancellation, any deposit or partial payment made by you will be fully refunded. After you have paid in full, we will only cancel the contract if circumstances beyond our control make it unavoidable. Such circumstances include, but are not limited to, civil or political unrest, terrorism, natural disaster, or other force majeure circumstance. In the unlikely event that such circumstances arise, we will contact you immediately and offer you the choice of equivalent services or a full refund of all monies paid. No additional compensation will be paid over and above the total sum received from you.',
        ],
    },
    {
        title: 'Alteration of Itineraries',
        content: [
            'It is unlikely that the Company will have to make changes to your tour. However, we may occasionally have to make changes either before or after you have booked. Most changes will be minor, and the Company will advise you of them as soon as possible. The Company reserves the right to alter the itinerary after departure, without paying compensation, if it is in your interest to do so. Furthermore, the Company will not pay compensation if it is forced to cancel or in any way change the tour due to force majeure, such as war, riots, civil strike, industrial dispute, terrorist activity, natural or nuclear disaster, fire, adverse weather conditions, or other material external circumstances beyond the Company\'s control.',
        ],
    },
    {
        title: 'Customer Complaints Policy',
        content: [
            'We are committed to providing high-quality services and ensuring a positive experience for all our customers. If you are dissatisfied with any aspect of our services, we encourage you to inform us so that we can address the issue.',
        ],
        subsections: [
            {
                subtitle: 'How to submit a complaint',
                content: 'Customers may submit a complaint through one of the following channels:',
                list: [
                    'By email',
                    'By phone or messaging applications',
                    'Via the customer satisfaction form',
                    'Through our local representative or guide',
                ],
                note: 'When submitting a complaint, please include your name, contact details, booking reference (if applicable), and a clear description of the issue.',
            },
            {
                subtitle: 'Handling of complaints',
                content: 'All complaints are recorded and handled in a confidential manner. We aim to acknowledge receipt of the complaint within 1–2 working days and to provide a full response within 10 working days. Where necessary, we will contact relevant service providers (such as accommodation providers, transport companies, guides, or other partners) to investigate the matter and take appropriate corrective action.',
            },
            {
                subtitle: 'Our commitment',
                content: 'We handle all complaints fairly, objectively, and transparently. Depending on the circumstances, appropriate measures may include an explanation, an apology, corrective actions, or other reasonable solutions.',
            },
            {
                subtitle: 'Continuous improvement',
                content: 'Complaints are reviewed regularly and are used as an opportunity to improve our services and enhance customer satisfaction.',
            },
        ],
    },
];

const Terms = () => {
    return (
        <>
            <div className="bg-dark min-h-screen py-24 md:py-32">
                <div className="container max-w-3xl">
                    {/* Header */}
                    <div className="flex flex-col gap-4 mb-16">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-px bg-it-green opacity-90" />
                            <p className="font-unbounded text-it-green text-xs uppercase tracking-widest">Legal</p>
                        </div>
                        <h1 className="font-unbounded font-bold text-4xl md:text-5xl text-white leading-tight">
                            Booking Terms<br />
                            <span className="text-gold">&amp; Conditions</span>
                        </h1>
                        <p className="font-manrope text-white/55 text-base leading-relaxed mt-2">
                            Tours are operated by the Travel Land, hereafter referred to as 'the Company'.
                            Below is a summary of the main terms and conditions that apply to your booking.
                            For full terms and conditions, please see the PDF file.
                        </p>
                        <div className="w-full h-px bg-white/10 mt-4" />
                    </div>

                    {/* Sections */}
                    <div className="flex flex-col gap-12">
                        {sections.map((section, i) => (
                            <div key={i} className="flex flex-col gap-4">
                                <h2 className="font-unbounded font-bold text-lg text-gold">
                                    {section.title}
                                </h2>

                                {section.content?.map((p, j) => (
                                    <p key={j} className="font-manrope text-white/65 text-sm leading-relaxed">
                                        {p}
                                    </p>
                                ))}

                                {section.list && (
                                    <ul className="flex flex-col gap-2 pl-4">
                                        {section.list.map((item, j) => (
                                            <li key={j} className="font-manrope text-white/65 text-sm leading-relaxed flex gap-3">
                                                <span className="text-gold mt-1.5 text-[6px]">&#9654;</span>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                {section.note && (
                                    <p className="font-manrope text-white/40 text-xs italic">{section.note}</p>
                                )}

                                {section.contentAfter?.map((p, j) => (
                                    <p key={j} className="font-manrope text-white/65 text-sm leading-relaxed">
                                        {p}
                                    </p>
                                ))}

                                {section.subsections?.map((sub, j) => (
                                    <div key={j} className="flex flex-col gap-3 pl-4 border-l border-white/10 mt-2">
                                        <h3 className="font-unbounded text-sm text-white/80">{sub.subtitle}</h3>
                                        {sub.content && (
                                            <p className="font-manrope text-white/65 text-sm leading-relaxed">{sub.content}</p>
                                        )}
                                        {sub.list && (
                                            <ul className="flex flex-col gap-2 pl-2">
                                                {sub.list.map((item, k) => (
                                                    <li key={k} className="font-manrope text-white/65 text-sm leading-relaxed flex gap-3">
                                                        <span className="text-gold mt-1.5 text-[6px]">&#9654;</span>
                                                        {item}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        {sub.note && (
                                            <p className="font-manrope text-white/55 text-sm leading-relaxed">{sub.note}</p>
                                        )}
                                    </div>
                                ))}

                                <div className="w-full h-px bg-white/5 mt-2" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default Terms;
